import PostCard from '@/components/PostCard';
import {
  fetchPlace,
  fetchPostsForPlace,
  subscribeToPlace,
  unsubscribeFromPlace,
} from '@/services/supabase-service';
import { Place, Post } from '@/types/type';
import { FontAwesome, MaterialCommunityIcons } from '@expo/vector-icons';
import { router, useLocalSearchParams } from 'expo-router';
import React, { useCallback, useEffect, useState } from 'react';
import { FlatList, SafeAreaView, Text, TouchableOpacity, View } from 'react-native';

const PlaceDetailsScreen = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubscribed, setSubscibed] = useState(false);
  const PAGE_SIZE = 5;

  const [currentPlace, setCurrentPlace] = useState<Place | null>(null);
  const { route, id } = useLocalSearchParams();

  useEffect(() => {
    const fetchData = async () => {
      const placeId = Array.isArray(id) ? id[0] : id;
      const selectedPlace = await fetchPlace(placeId);
      setCurrentPlace(selectedPlace);
    };
    fetchData();
  }, [id]);

  const loadMore = async () => {
    if (!loading && hasMore) {
      console.log('Loading more posts');
      setLoading(true);
      const newPosts = await fetchPostsForPlace(currentPlace.id, PAGE_SIZE, posts.length);
      setPosts((prev) => [...prev, ...newPosts]);
      setHasMore(newPosts.length > 0);
      setLoading(false);
    }
  };

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    setLoading(true);
    const newPosts = await fetchPostsForPlace(currentPlace.id, PAGE_SIZE, 0);
    setPosts(newPosts);
    setHasMore(newPosts.length > 0);
    setRefreshing(false);
    setLoading(false);
  }, [currentPlace]);

  const isLongText = currentPlace?.description ? currentPlace.description.length > 200 : false;

  const OnSubscribePress = () => {
    if (currentPlace && currentPlace.id) {
      if (isSubscribed) {
        unsubscribeFromPlace(currentPlace.id);
        setSubscibed(false);
        currentPlace.subscribed = false;
        console.log('Unsubscribed');
      } else {
        subscribeToPlace(currentPlace.id);
        currentPlace.subscribed = true;
        setSubscibed(true);
        console.log('Subscribed');
      }
    }
  };

  useEffect(() => {
    const Subscription = async () => {
      if (currentPlace && currentPlace.id) {
        setSubscibed(currentPlace.subscribed ?? false);
      }
    };
    Subscription();
  }, [currentPlace]);

  if (!currentPlace) {
    return (
      <SafeAreaView className="h-full w-full">
        <Text>Place not found</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="h-full w-full">
      {currentPlace.id && (
        <FlatList
          ListHeaderComponent={
            <View className="items-center">
              <View style={{ padding: 16 }}>
                <View className="mb-4">
                  <Text style={{ fontSize: 40, fontWeight: 'bold' }}>{currentPlace?.title}</Text>
                </View>
                <Text>{currentPlace?.legal_notices}</Text>

                <View style={{ marginBottom: 24 }}>
                  <Text
                    style={{ fontSize: 14, lineHeight: 20 }}
                    numberOfLines={isExpanded ? undefined : 3}>
                    {currentPlace?.description}
                  </Text>
                  {isLongText && (
                    <TouchableOpacity onPress={() => setIsExpanded(!isExpanded)}>
                      <Text style={{ color: '#003566', marginTop: 4 }}>
                        {isExpanded ? ' Read Less...' : ' Read More...'}
                      </Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>

              <TouchableOpacity className="absolute top-4 right-4" onPress={OnSubscribePress}>
                {isSubscribed ? (
                  <FontAwesome name="bookmark" size={24} color="black" />
                ) : (
                  <FontAwesome name="bookmark-o" size={24} color="black" />
                )}
              </TouchableOpacity>
              <TouchableOpacity
                className="absolute top-10 right-4"
                onPress={() => {
                  router.push(`/comments?id=${currentPlace.id}&type=place`);
                }}>
                <FontAwesome name="comment" size={24} color="black" />
              </TouchableOpacity>
            </View>
          }
          contentContainerStyle={{ paddingBottom: 70 }}
          data={posts}
          renderItem={({ item }) => <PostCard key={item.id} post={item} />}
          keyExtractor={(item) => item.id?.toString() || String(Math.random())}
          onEndReached={loadMore}
          onEndReachedThreshold={0}
          refreshing={refreshing}
          onRefresh={onRefresh}
        />
      )}
      <TouchableOpacity
        onPress={() => {
          router.push(`./../post-create?place_id=${id}`);
        }}
        className="absolute bottom-0 right-1 p-4 bg-blue-500 rounded-full">
        <MaterialCommunityIcons name="plus" size={24} />
      </TouchableOpacity>
    </SafeAreaView>
  );
};

export default PlaceDetailsScreen;
